(() => {
  if (window.__swv8DeferredMediaLoaded) return;
  window.__swv8DeferredMediaLoaded = true;
  const fragmentSelector = '[data-swv8-deferred-fragment]';
  const idleImageSelector = '[data-swv8-idle-image]';
  const videoSelector = '[data-swv8-deferred-video]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fragmentObservers = new WeakMap();
  const videoObservers = new WeakMap();
  const queuedImages = new Set();
  let idleHandle = null;
  let idleHandleType = '';
  let idleBackfillStarted = false;

  const initializeLoox = (kind, attempt = 0) => {
    if (kind !== 'loox-carousel') return;
    if (window.LOOX?.initLooxCarouselV2) {
      window.LOOX.initLooxCarouselV2();
      return;
    }
    if (attempt < 50) window.setTimeout(() => initializeLoox(kind, attempt + 1), 100);
  };

  const hydrateFragment = (root) => {
    if (!root || root.dataset.swv8Hydrated === 'true') return;
    const template = root.querySelector(':scope template[data-swv8-deferred-template]');
    if (!template) return;
    const fragment = template.content.cloneNode(true);
    const deferredScripts = [];
    fragment.querySelectorAll('script').forEach((original) => {
      const marker = document.createComment('swv8-deferred-script');
      original.replaceWith(marker);
      deferredScripts.push({ marker, original });
    });
    root.dataset.swv8Hydrated = 'true';
    fragmentObservers.get(root)?.disconnect();
    fragmentObservers.delete(root);
    root.append(fragment);
    template.remove();
    deferredScripts.forEach(({ marker, original }) => {
      const script = document.createElement('script');
      Array.from(original.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
      script.textContent = original.textContent;
      marker.replaceWith(script);
    });
    root.classList.add('is-hydrated');
    root.setAttribute('aria-busy', 'false');
    initializeLoox(root.dataset.swv8DeferredFragment);
  };

  const cancelIdleHandle = () => {
    if (idleHandle === null) return;
    if (idleHandleType === 'idle') window.cancelIdleCallback?.(idleHandle);
    else window.clearTimeout(idleHandle);
    idleHandle = null;
    idleHandleType = '';
  };

  const hydrateIdleImage = (root) => {
    if (!root || root.dataset.swv8ImageHydrated === 'true') return;
    const image = root.matches('img') ? root : root.querySelector('img');
    root.dataset.swv8ImageHydrated = 'true';
    queuedImages.delete(root);
    if (!image || image.complete) return;
    image.fetchPriority = 'low';
    image.loading = 'eager';
    image.decode?.().catch(() => {});
  };

  const scheduleIdleHydration = () => {
    if (idleHandle !== null || !queuedImages.size) return;
    const run = () => {
      idleHandle = null;
      idleHandleType = '';
      const nextImage = Array.from(queuedImages).find((root) => root.isConnected && root.dataset.swv8ImageHydrated !== 'true');
      if (nextImage) hydrateIdleImage(nextImage);
      Array.from(queuedImages).forEach((root) => {
        if (!root.isConnected || root.dataset.swv8ImageHydrated === 'true') queuedImages.delete(root);
      });
      scheduleIdleHydration();
    };
    if ('requestIdleCallback' in window) {
      idleHandleType = 'idle';
      idleHandle = window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      idleHandleType = 'timeout';
      idleHandle = window.setTimeout(run, 3000);
    }
  };

  const startIdleBackfill = () => {
    if (idleBackfillStarted) return;
    idleBackfillStarted = true;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    document.querySelectorAll(idleImageSelector).forEach((root) => {
      if (root.dataset.swv8ImageHydrated !== 'true') queuedImages.add(root);
    });
    scheduleIdleHydration();
  };

  const hydrateVideo = (video) => {
    if (!video || video.dataset.swv8Hydrated === 'true') return;
    const sources = video.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.swv8Hydrated = 'true';
    if (sources.length) video.load();
  };

  const observeFragment = (root) => {
    if (root.dataset.swv8Hydrated === 'true' || fragmentObservers.has(root)) return;
    if (!('IntersectionObserver' in window)) {
      hydrateFragment(root);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      hydrateFragment(root);
      observer.disconnect();
      fragmentObservers.delete(root);
    }, { rootMargin: '1400px 0px', threshold: 0 });
    observer.observe(root);
    fragmentObservers.set(root, observer);
  };

  const observeVideo = (video) => {
    if (videoObservers.has(video)) return;
    if (!('IntersectionObserver' in window)) {
      hydrateVideo(video);
      return;
    }
    const preloadObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      hydrateVideo(video);
      preloadObserver.disconnect();
    }, { rootMargin: '700px 0px', threshold: 0 });
    const playbackObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !reducedMotion.matches) {
          hydrateVideo(video);
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.2 });
    preloadObserver.observe(video);
    playbackObserver.observe(video);
    videoObservers.set(video, [preloadObserver, playbackObserver]);
  };

  const initialize = (scope = document) => {
    const roots = scope.matches?.(fragmentSelector) ? [scope] : scope.querySelectorAll?.(fragmentSelector) || [];
    const images = scope.matches?.(idleImageSelector) ? [scope] : scope.querySelectorAll?.(idleImageSelector) || [];
    const videos = scope.matches?.(videoSelector) ? [scope] : scope.querySelectorAll?.(videoSelector) || [];
    roots.forEach((root) => {
      observeFragment(root);
    });
    images.forEach((root) => {
      if (idleBackfillStarted && root.dataset.swv8ImageHydrated !== 'true') queuedImages.add(root);
    });
    videos.forEach(observeVideo);
    if (idleBackfillStarted) scheduleIdleHydration();
  };

  const findReviewRoot = () => Array.from(
    document.querySelectorAll('[data-swv8-deferred-anchor="looxReviewsFrame"]')
  ).find((root) => root.isConnected);

  const scrollToReviews = (root, attempt = 0) => {
    const target = root.querySelector('#looxReviewsFrame, #looxReviews') || root;
    const top = target.getBoundingClientRect().top + window.scrollY - 96;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: attempt === 0 && !reduced ? 'smooth' : 'auto' });
    if (target.getBoundingClientRect().height > 100 || attempt >= 50) {
      target.setAttribute('tabindex', '-1');
      window.setTimeout(() => target.focus({ preventScroll: true }), 250);
      return;
    }
    window.setTimeout(() => scrollToReviews(root, attempt + 1), 100);
  };

  const teardown = (scope) => {
    scope.querySelectorAll?.(fragmentSelector).forEach((root) => {
      fragmentObservers.get(root)?.disconnect();
      fragmentObservers.delete(root);
    });
    scope.querySelectorAll?.(idleImageSelector).forEach((root) => queuedImages.delete(root));
    scope.querySelectorAll?.(videoSelector).forEach((video) => {
      videoObservers.get(video)?.forEach((observer) => observer.disconnect());
      videoObservers.delete(video);
      video.pause();
    });
  };

  initialize();
  if (document.readyState === 'complete') startIdleBackfill();
  else window.addEventListener('load', startIdleBackfill, { once: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    initialize(event.target);
    event.target.querySelectorAll?.(fragmentSelector).forEach(hydrateFragment);
    event.target.querySelectorAll?.(idleImageSelector).forEach(hydrateIdleImage);
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#looxReviews"], a[href="#looxReviewsFrame"]');
    if (!link) return;
    const root = findReviewRoot();
    if (!root) return;
    event.preventDefault();
    hydrateFragment(root);
    window.history.replaceState(null, '', '#looxReviewsFrame');
    scrollToReviews(root);
  }, { capture: true });
  window.addEventListener('pagehide', cancelIdleHandle, { once: true });
})();
